import { useMutation, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import React, { useContext, useState } from "react";
import uuid from "uuid";

import { schemaParams } from "@examspark/api";
import { SystemTag } from "@examspark/common";
import { assertNativeError } from "@examspark/common/dist/utils";
import { Modal, ModalProps, SubmitButtons } from "@examspark/common-ui";
import { toast } from "@examspark/common-ui/dist/components/toast/Toast";
import { CreateClassroomInput, Subject } from "@examspark/types";

import { useQueryGetAllCalendarsForCourse } from "../../../api/operations/calendar";
import {
  useMutationCreateClassroom,
  useMutationCreateUserClassroom,
} from "../../../api/operations/classroom";
import { useQueryGetCourse } from "../../../api/operations/course";
import { useAppContext } from "../../../providers/AppContext";
import { LTIContext } from "../../../providers/LTIProvider";

import NewClassroomForm, { NewClassroomFormData } from "./NewClassroomForm";

export const useMutationCreateClassroomAndUser = () => {
  const [state] = useAppContext();
  const lti = useContext(LTIContext);
  const createClassroom = useMutationCreateClassroom();
  const createUserClassroom = useMutationCreateUserClassroom();
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      hideCourseInput?: boolean;
      hideGradeInput?: boolean;
      hideSubjectInput?: boolean;
      form: NewClassroomFormData;
    }) => {
      const { form, hideGradeInput, hideSubjectInput, hideCourseInput } = input;

      if (form.name.trim().length < 3) {
        throw new Error("Classroom name must be at least 3 characters long");
      }
      if (!state.user?.id) {
        throw new Error("No user id found");
      }
      if (!state.workspaceUser?.workspace?.id) {
        throw new Error("No workspace id found");
      }
      if (!state.user?.id) {
        console.error("No user id found");
        return;
      }
      if (!hideGradeInput && !form.grade) {
        throw new Error("Please select a grade");
      }
      if (!hideSubjectInput && !form.subject) {
        throw new Error("Please select a subject");
      }

      const classroomId = uuid.v4();
      const promises: any[] = [];

      const classroomInput: CreateClassroomInput = {
        id: classroomId,
        sk: "classroom",
        name: form.name,
        sectionNumber: form.sectionNumber,
        room: form.room,
        courseId: form.course,
        // grade: [form.grade],
        // subject: [form.subject],
        ...schemaParams(),
        creatorId: state.user.id,
        workspaceId: state.workspaceUser.workspace.id,
        tags: [
          {
            key: SystemTag.UseCaseInsensitiveStudentIds,
            type: "string",
            value: "true",
          },
        ],
      };

      if (lti) {
        if (!classroomInput.tags) classroomInput.tags = [];
        classroomInput.tags.push(
          ...[
            {
              key: "lti-product",
              type: "string",
              value: lti?.token.data?.tool_consumer_product,
            },
            {
              key: SystemTag.LtiContextId,
              type: "string",
              value: lti?.token.data?.context_id,
            },
            {
              key: "lti-context-title",
              type: "string",
              value: lti?.token.data?.context_title,
            },
            {
              key: "lti-context-label",
              type: "string",
              value: lti?.token.data?.context_label,
            },
          ],
        );
      }

      if (!hideCourseInput) {
        classroomInput.courseId = form.course;
      }
      if (form.calendar) {
        classroomInput.calendarId = [form.calendar?.id, form.calendar?.sk].join(
          "/",
        );
      }
      if (!hideGradeInput) {
        // these are redundant but makes the type system happy
        if (!form.grade) {
          throw new Error("Please select a grade");
        }
        classroomInput.grade = [form.grade];
      }
      if (!hideSubjectInput) {
        if (!form.subject) {
          throw new Error("Please select a subject");
        }
        classroomInput.subject = [form.subject];
      }

      promises.push(createClassroom.mutateAsync(classroomInput));
      promises.push(
        createUserClassroom.mutateAsync({
          userId: state.user.id,
          classroomId: classroomId,
          workspaceId: state.workspaceUser.workspace.id,
        }),
      );

      await Promise.all(promises);
      client.invalidateQueries(["classrooms"]);
    },
  });
};

const NewClassroomManual = (
  props: Omit<ModalProps, "isOpen"> & {
    hideCourseInput?: boolean;
    hideGradeInput?: boolean;
    hideSubjectInput?: boolean;
    allowedSubjects?: string[];
    overrideSubjects?: Subject[];
    onCreate: () => void;
    onBack: () => void;
  },
) => {
  const [appState] = useAppContext();
  const createClassroom = useMutationCreateClassroomAndUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<NewClassroomFormData>({
    name: "",
    room: "",
    sectionNumber: "",
  });
  const courseResult = useQueryGetCourse(form.course);
  const calendarsQuery = useQueryGetAllCalendarsForCourse(courseResult.data);
  const calendarsEnabled =
    appState.app?.config?.capabilities?.calendarsEnabled ?? false;

  const shouldHaveCalendar =
    calendarsEnabled &&
    (calendarsQuery.isLoading || calendarsQuery.data?.length !== 0);

  let canSubmit = form.name.trim().length > 0;
  if (!props.hideCourseInput) {
    canSubmit &&= form.course != null && courseResult.data != null;
  }
  if (!props.hideGradeInput) {
    canSubmit &&= form.grade != null;
  }
  if (!props.hideSubjectInput) {
    canSubmit &&= form.subject != null;
  }
  // todo: allow calendars outside of course-based?
  if (shouldHaveCalendar && !props.hideCourseInput) {
    canSubmit &&= form.calendar != null;
  }

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await createClassroom.mutateAsync({
        form: produce(form, (draft) => {
          draft.name = draft.name.trim();
          draft.room = draft.room.trim();
          draft.sectionNumber = draft.sectionNumber.trim();
        }),
        hideGradeInput: props.hideGradeInput,
        hideSubjectInput: props.hideSubjectInput,
      });
      toast.success("Classroom created");

      props.onCreate();
    } catch (error) {
      assertNativeError(error);
      alert(error.message);
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Modal.Body>
        <div style={{ padding: "1rem" }}>
          <NewClassroomForm
            hideCourseInput={props.hideCourseInput}
            hideGradeInput={props.hideGradeInput}
            hideSubjectInput={props.hideSubjectInput}
            allowedSubjects={props.allowedSubjects}
            overrideSubjects={props.overrideSubjects}
            onChange={(value) => setForm(value)}
            value={form}
          />
        </div>
      </Modal.Body>
      <Modal.BasicFooter>
        <SubmitButtons
          onSubmit={handleSubmit}
          isSubmitDisabled={!canSubmit}
          isSubmitting={isSubmitting}
          submitLabel="Create Classroom"
          onCancel={props.onBack}
          cancelLabel="Back"
        />
      </Modal.BasicFooter>
    </>
  );
};

export default NewClassroomManual;
